import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ChevronLeft, Save } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../contexts/AuthContext';
import { WIZARD_STEPS } from '../lib/listingConstants';
import StepStart from '../components/wizard/StepStart';
import StepPropertyType from '../components/wizard/StepPropertyType';
import StepLocation from '../components/wizard/StepLocation';
import StepPhotos from '../components/wizard/StepPhotos';
import StepPricing from '../components/wizard/StepPricing';
import StepLease from '../components/wizard/StepLease';
import StepAmenities from '../components/wizard/StepAmenities';
import StepRules from '../components/wizard/StepRules';
import StepDescription from '../components/wizard/StepDescription';
import StepReview from '../components/wizard/StepReview';
import StepSubmitted from '../components/wizard/StepSubmitted';

const STEP_COMPONENTS = {
  start: StepStart,
  'property-type': StepPropertyType,
  location: StepLocation,
  photos: StepPhotos,
  pricing: StepPricing,
  lease: StepLease,
  amenities: StepAmenities,
  rules: StepRules,
  description: StepDescription,
  review: StepReview,
};

/**
 * Wizard shell. Mounts at /listings/new/:listing_id/:step.
 *
 * Responsibilities:
 *   - Ensure a draft exists (auto-creates on /listings/new/new/start).
 *   - Fetch the listing once and keep it in local state (source of truth).
 *   - Render the current step component; each step reports back via `onSave`.
 *   - Auto-save sends PATCH /api/listings/draft/:id and updates local state.
 *   - Top progress bar + back + "Save and exit".
 */
const ListingWizard = () => {
  const { listing_id, step } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStep, setSavingStep] = useState(false);

  const stepIdx = useMemo(
    () => Math.max(0, WIZARD_STEPS.findIndex((s) => s.key === step)),
    [step]
  );
  const stepMeta = WIZARD_STEPS[stepIdx] || WIZARD_STEPS[0];

  // Role guard — redirect students to the friendly "landlord-only" page.
  useEffect(() => {
    if (user && user.role !== 'landlord' && user.role !== 'admin') {
      navigate('/landlord-only', { replace: true });
    }
  }, [user, navigate]);

  // Bootstrap: create a draft if the id is literally "new" or missing, else fetch.
  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      try {
        if (!listing_id || listing_id === 'new') {
          // No id yet — create a fresh draft and redirect to its first step.
          const res = await api.post('/listings/new');
          if (cancelled) return;
          navigate(`/listings/new/${res.data.listing_id}/start`, { replace: true });
          return;
        }
        const res = await api.get(`/listings/draft/${listing_id}`);
        if (cancelled) return;
        setListing(res.data.listing);
      } catch (e) {
        if (cancelled) return;
        const status = e.response?.status;
        if (status === 403) {
          // Student hit the landlord-only gate — send to friendly redirect.
          navigate('/landlord-only', { replace: true });
          return;
        }
        toast.error("We couldn't load that draft. It may have been removed.");
        navigate('/home', { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    bootstrap();
    return () => { cancelled = true; };
  }, [listing_id, api, navigate]);

  const goTo = (key) => navigate(`/listings/new/${listing_id}/${key}`);
  const nextStep = () => {
    if (stepIdx < WIZARD_STEPS.length - 1) {
      goTo(WIZARD_STEPS[stepIdx + 1].key);
    }
  };
  const prevStep = () => {
    if (stepIdx > 0) {
      goTo(WIZARD_STEPS[stepIdx - 1].key);
    } else {
      navigate('/home');
    }
  };

  const patchDraft = async (updates) => {
    setSavingStep(true);
    try {
      const res = await api.patch(`/listings/draft/${listing_id}`, updates);
      setListing(res.data.listing);
      return res.data.listing;
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || 'Save failed.';
      toast.error(msg);
      throw e;
    } finally {
      setSavingStep(false);
    }
  };

  const refetch = async () => {
    const res = await api.get(`/listings/draft/${listing_id}`);
    setListing(res.data.listing);
    return res.data.listing;
  };

  const saveAndExit = async () => {
    toast.success('Draft saved. Come back anytime to finish.');
    navigate('/home');
  };

  if (loading || !listing) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
      </div>
    );
  }

  // /submitted is a special end state, but it uses the same shell layout.
  if (step === 'submitted') {
    return <StepSubmitted listing={listing} />;
  }

  const StepComp = STEP_COMPONENTS[step] || STEP_COMPONENTS.start;
  const percent = Math.round(((stepIdx + 1) / WIZARD_STEPS.length) * 100);

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="listing-wizard">
      <AppHeader />
      <div className="sticky top-[60px] z-30 bg-background border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={prevStep}
            className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900"
            data-testid="wizard-back-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              Step {stepIdx + 1} of {WIZARD_STEPS.length}
            </div>
            <div className="text-sm font-heading font-bold text-slate-900 truncate">
              {stepMeta.label}
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={saveAndExit}
            className="text-slate-700 hover:bg-slate-100"
            data-testid="wizard-save-exit-btn"
          >
            <Save className="h-3.5 w-3.5 mr-1.5" /> Save &amp; exit
          </Button>
        </div>
        <Progress value={percent} className="h-1 rounded-none" />
      </div>

      <main className="flex-1 px-4 sm:px-6 py-5 sm:py-8" data-testid={`wizard-step-${step}`}>
        <div className="max-w-2xl mx-auto">
          <StepComp
            listing={listing}
            patchDraft={patchDraft}
            refetch={refetch}
            onNext={nextStep}
            goTo={goTo}
            saving={savingStep}
          />
        </div>
      </main>
    </div>
  );
};

export default ListingWizard;
